import { useEffect,useState } from 'react';

import "./styles/base.css";
import "./styles/display.css";

export default function PollDisplay({
  pollId,
  title,
  options: initialOptions,
  onVote,
  onEditClick,
  previewMode = false,
  initialHasVoted = false,
  initialUserAnswer = null, 
  hasAnybodyAnswered = false,
}) {
  const [options, setOptions] = useState(initialOptions);
  const [hasVoted, setHasVoted] = useState(initialHasVoted);
  const [selectedIndex, setSelectedIndex] = useState(initialUserAnswer);

  const canEdit = !previewMode && Boolean(onEditClick) && !hasAnybodyAnswered;

  // whenever the parent gives us new options, reset our state
  useEffect(() => {
    setOptions(initialOptions);
  }, [initialOptions]);

  // whenever the parent's "hasVoted" flag changes, reset too
  useEffect(() => {
    setHasVoted(initialHasVoted);
    // also reset our selectedIndex if they've already answered
    setSelectedIndex(initialHasVoted ? initialUserAnswer : null);
  }, [initialHasVoted, initialUserAnswer]);

  const getOptionText = option => (
    typeof option === 'string' ? option : option?.text ?? ''
  );
  const getOptionVotes = option => (
    typeof option === 'string' ? 0 : option?.votes ?? 0
  );
  const totalVotes = options.reduce((sum, option) => sum + getOptionVotes(option), 0) || 1;

  const handleVote = async (idx) => {
    if (!onVote) return;
    try {
      const updatedOptions = await onVote(pollId, idx);
      setOptions(updatedOptions);
      setHasVoted(true);
      setSelectedIndex(idx);
    } catch (err) {
      // if user already voted, server returns 403
      if (err.response?.status === 403) {
        setHasVoted(true);
        // we don't really know which they'd picked, so leave as-is
      }
      throw err;              // still bubble up OTHER errors
    }
  };

  return (
    <div className="poll-wrapper" contentEditable={false}>
      <div
        className={`poll-container${canEdit ? ' poll-container--editable' : ''}`}
        {...(canEdit ? {
          role: 'button',
          tabIndex: 0,
          'aria-label': 'Edit poll',
          onClick: onEditClick,
          onKeyDown: event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onEditClick();
            }
          }
        } : {})}
      >
        <h3 className="poll-title">{title}</h3>
        <div className="poll-options-container">
          {!hasVoted && onVote && !canEdit
            ? options.map((opt, i) => {
              const text = getOptionText(opt);
              return (
                <button
                  key={i}
                  className={`submitted-poll-option-input${
                    selectedIndex === i ? ' selected' : ''
                  }`}
                  onClick={e => {
                    if (canEdit) return;
                    e.stopPropagation(); // Prevent click from bubbling up
                    handleVote(i);
                  }}
                >
                  <span>{text}</span>
                </button>
              );
            })
            : options.map((opt, i) => {
              const text = getOptionText(opt);
              const votes = getOptionVotes(opt);
              return (
                <div
                  key={i}
                  className={`submitted-poll-option-input${
                    selectedIndex === i ? ' selected' : ''
                  }`}
                >
                  <span>{text}</span>
                  <span>{Math.round((votes / totalVotes) * 100)}%</span>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
